/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { ArrowLeft, Video, ClipboardCheck, StickyNote, Loader2, Send, Phone, FileText, Gamepad2 } from "lucide-react";
import { downloadPdf } from "../../lib/pdf";
import { TelesoinPanel } from "../../components/telesoin/TelesoinPanel";
import { ActesCard } from "../../components/telesoin/ActesCard";
import { ProgrammeBuilder, PainCard } from "../../components/programme/ProgrammeBuilder";
import { GoalsForm } from "../../components/Goals";
import { ProtocolProgress } from "../../components/ProtocolProgress";
import { VitalsTargetsForm } from "../../components/VitalsTab";
import { VoiceBubble, VoiceRecorder } from "../../components/VoiceMessage";
import { MealPhotosCard } from "../../components/MealPhotos";
import { MealScoreCard, MealGoalForm, MealHistoryChart } from "../../components/MealScore";
import { WorryList } from "./Worries";

const Card = ({ title, children, testid, right }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#E4E0D5] p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">{title}</h3>{right}
    </div>
    {children}
  </div>
);

const DOM = { equilibre: "Équilibre & mémoire", marche: "Marche & équilibre", avq: "Gestes du quotidien" };
const MOODS = ["", "😞", "😕", "😐", "🙂", "😄"];

function Thread({ patient }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const threadId = patient.user_id ? `${patient.user_id}__${user.id}` : null;
  const load = () => threadId && api.get(`/messages?thread_id=${threadId}`).then(({ data }) => setMsgs(data)).catch(() => null);
  useEffect(() => { load(); }, [threadId]); // eslint-disable-line
  const send = async () => {
    if (!text.trim()) return;
    await api.post("/messages", { thread_id: threadId, destinataire_id: patient.user_id, texte: text.trim() });
    setText(""); load();
  };
  return (
    <Card title="Messagerie" testid="pro-thread">
      <div className="max-h-56 overflow-y-auto flex flex-col gap-1.5 mb-2">
        {msgs.map((m) => m.type === "vocal"
          ? <div key={m.id} className={m.expediteur_id === user.id ? "self-end" : "self-start"}><VoiceBubble msg={m} mine={m.expediteur_id === user.id} onRefresh={load} /></div>
          : <div key={m.id} className={`max-w-[85%] px-3 py-1.5 rounded-2xl text-[13px] ${m.expediteur_id === user.id ? "self-end bg-[#2BA3A0] text-white" : "self-start bg-[#F5F3EE] text-[#0A1426]"}`}>{m.texte}</div>
        )}
        {msgs.length === 0 && <div className="text-[12px] text-[#0A1426]/60">Aucun message.</div>}
      </div>
      <div className="flex gap-2 items-center">
        <VoiceRecorder threadId={threadId} destinataireId={patient.user_id} size="sm" onSent={() => load()} />
        <input data-testid="pro-msg-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Écrire au patient…"
          className="flex-1 h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426] focus:outline-none focus:ring-2 focus:ring-[#2BA3A0]" />
        <button data-testid="pro-msg-send" onClick={send} className="w-11 h-11 rounded-full bg-[#2BA3A0] text-white grid place-items-center"><Send size={16} /></button>
      </div>
    </Card>
  );
}

function PatientWorries({ patientId }) {
  const [data, setData] = useState(null);
  const load = () => api.get(`/therapist/worries?patient_id=${patientId}`).then(({ data }) => { setData(data); window.dispatchEvent(new CustomEvent("bc-worries-changed")); }).catch(() => null);
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line
  if (!data || data.total === 0) return null;
  return (
    <div data-testid="patient-worries" className={`rounded-2xl border p-4 ${data.ouverts ? "bg-[#D9534F]/8 border-[#D9534F]/30" : "bg-white border-[#E4E0D5]"}`}>
      <h3 className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold mb-3">Signalements « Je suis inquiet » ({data.ouverts} à traiter / {data.total})</h3>
      <WorryList items={data.items.slice(0, 5)} onChange={load} compact />
    </div>
  );
}


export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [score, setScore] = useState("");
  const [saving, setSaving] = useState(false);
  const [tele, setTele] = useState(false);
  const [prog, setProg] = useState(false);
  const [actesKey, setActesKey] = useState(0);

  const load = () => api.get(`/therapist/patients/${id}/summary`).then(({ data }) => setS(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const saveNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post("/clinical-notes", { patient_id: id, texte: note.trim(), type: noteType, score_autonomie: noteType === "bilan" && score !== "" ? Number(score) : null });
      setNote(""); setScore(""); toast.success(noteType === "bilan" ? "Bilan validé" : "Note ajoutée"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const exportPdf = async () => {
    try { await downloadPdf(id, `bombacare-${s.patient.nom}.pdf`); toast.success("PDF téléchargé"); }
    catch (e) { toast.error(e.response?.status === 402 ? "Export PDF réservé au forfait Clinique" : "Export impossible"); }
  };

  const teleChanged = () => { setActesKey((k) => k + 1); load(); };

  if (!s) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div>;
  const p = s.patient;
  const bpData = s.bp.map((b) => ({ d: new Date(b.datetime).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }), sys: b.systolique, dia: b.diastolique }));
  const moodData = s.humeur.map((m) => ({ d: new Date(m.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }), score: m.score }));
  const totalSeances = Object.values(s.seances_par_domaine).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="bc-in flex flex-col gap-5" data-testid="pro-patient-detail">
      <div className="flex items-center gap-3">
        <button data-testid="patient-back" onClick={() => navigate("/pro")} className="w-11 h-11 grid place-items-center rounded-full bg-white border border-[#E4E0D5] text-[#0A1426]"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="font-[Fraunces] text-2xl md:text-3xl font-bold text-[#0A1426]">{p.nom}</h1>
          <div className="text-[12px] text-[#0A1426]/60">{p.diagnostic} · côté {p.cote_atteint} · AVC le {p.date_avc ? new Date(p.date_avc).toLocaleDateString("fr-FR") : "—"}</div>
        </div>
        <Button data-testid="export-pdf-pro-btn" variant="outline" onClick={exportPdf} className="h-11 rounded-full border-[#0A1426] text-[#0A1426] font-bold hidden sm:inline-flex"><FileText size={16} className="mr-2" /> PDF</Button>
        <Button data-testid="patient-jeu-btn" variant="outline" onClick={() => navigate(`/pro/patients/${id}/jeu`)} className="h-11 rounded-full border-[#2BA3A0] text-[#0A1426] font-bold"><Gamepad2 size={16} className="mr-2" /> Jeu ↔ Clinique</Button>
        <Button data-testid="programme-builder-btn" onClick={() => setProg(true)} className="h-11 rounded-full bg-[#2BA3A0] text-white font-bold hidden sm:inline-flex">Programme &lt; 30 s</Button>
        <Button data-testid="start-telesoin-btn" onClick={() => setTele(true)} className="h-11 rounded-full bg-[#0A1426] text-white font-bold hidden sm:inline-flex"><Video size={16} className="mr-2" /> Télésoin</Button>
      </div>

      {s.alertes.length > 0 && (
        <div data-testid="patient-alerts" className="flex flex-wrap gap-2">
          {s.alertes.map((a, i) => <span key={i} className={`text-[12px] font-bold px-3 py-1.5 rounded-full ${a.niveau === "haut" ? "bg-[#D9534F]/15 text-[#b23a36]" : "bg-[#D4AF5A]/20 text-[#8A6D2B]"}`}>{a.texte}</span>)}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Score d'autonomie", `${p.score_autonomie ?? "—"}/100`], ["Observance 7 j", s.observance_pct == null ? "—" : `${s.observance_pct}%`], ["Séances", s.seances_total], ["Niveau · points", `${p.niveau} · ${p.points}`]].map(([l, v]) => (
          <div key={l} className="bg-white rounded-2xl border border-[#E4E0D5] p-4">
            <div className="font-[Fraunces] text-2xl font-bold text-[#0A1426]">{v}</div>
            <div className="text-[11px] text-[#0A1426]/60">{l}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title={`Tension artérielle (cible < ${p.cible_tension_sys_max}/${p.cible_tension_dia_max})`} testid="bp-chart">
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={bpData}>
                <XAxis dataKey="d" tick={{ fontSize: 10 }} /><YAxis domain={[60, 180]} tick={{ fontSize: 10 }} width={28} /><Tooltip />
                <ReferenceLine y={p.cible_tension_sys_max} stroke="#D9534F" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="sys" stroke="#0A1426" strokeWidth={2} dot={false} name="Systolique" />
                <Line type="monotone" dataKey="dia" stroke="#2BA3A0" strokeWidth={2} dot={false} name="Diastolique" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Humeur (1–5)" testid="mood-chart">
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={moodData}>
                <XAxis dataKey="d" tick={{ fontSize: 10 }} /><YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 10 }} width={20} /><Tooltip />
                <ReferenceLine y={2} stroke="#D4AF5A" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="score" stroke="#D4AF5A" strokeWidth={2} name="Humeur" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-1 mt-1 text-xl">{s.humeur.slice(-7).map((m, i) => <span key={i}>{MOODS[m.score]}</span>)}</div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card title="Progression par domaine" testid="domain-progress">
          {Object.keys(DOM).map((k) => { const n = s.seances_par_domaine[k] || 0; return (
            <div key={k} className="mb-2">
              <div className="flex justify-between text-[12px] text-[#0A1426]"><span>{DOM[k]}</span><b>{n}</b></div>
              <div className="h-2 bg-[#F5F3EE] rounded-full overflow-hidden"><div className="h-full bg-[#2BA3A0]" style={{ width: `${Math.round(n / totalSeances * 100)}%` }} /></div>
            </div>
          ); })}
          <div className="mt-3 text-[12px] text-[#0A1426]/70">Médicaments : {s.medicaments.map((m) => `${m.nom} ${m.dose || ""}`).join(", ") || "—"}</div>
          {s.aidants.length > 0 && <div className="mt-2 text-[12px] text-[#0A1426]/70 flex items-center gap-1"><Phone size={12} /> Aidant : {s.aidants.map((a) => `${a.nom} ${a.telephone || ""}`).join(", ")}</div>}
        </Card>

        <Card title="Note clinique / Bilan" testid="note-form">
          <div className="flex gap-2 mb-2">
            {[["note", StickyNote, "Note"], ["bilan", ClipboardCheck, "Valider un bilan"]].map(([k, I, l]) => (
              <button key={k} data-testid={`note-type-${k}`} onClick={() => setNoteType(k)} className={`flex-1 h-10 rounded-full text-[12px] font-bold inline-flex items-center justify-center gap-1 ${noteType === k ? "bg-[#0A1426] text-white" : "bg-[#F5F3EE] text-[#0A1426]"}`}><I size={14} />{l}</button>
            ))}
          </div>
          <Textarea data-testid="note-text" value={note} onChange={(e) => setNote(e.target.value)} placeholder={noteType === "bilan" ? "Synthèse du bilan…" : "Observation clinique…"} className="bg-[#F5F3EE] border-0 min-h-[90px] text-sm" />
          {noteType === "bilan" && (
            <input data-testid="note-score" type="number" min="0" max="100" value={score} onChange={(e) => setScore(e.target.value)} placeholder="Score d'autonomie /100"
              className="mt-2 h-11 w-full px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426]" />
          )}
          <Button data-testid="note-save" disabled={saving || !note.trim()} onClick={saveNote} className="w-full h-11 mt-2 rounded-full bg-[#2BA3A0] text-white font-bold">
            {saving ? <Loader2 className="animate-spin" size={16} /> : noteType === "bilan" ? "Valider le bilan" : "Ajouter la note"}
          </Button>
          <Button data-testid="start-telesoin-btn-mobile" onClick={() => setTele(true)} variant="outline" className="w-full h-11 mt-2 rounded-full border-[#0A1426] text-[#0A1426] font-bold sm:hidden"><Video size={16} className="mr-2" /> Télésoin</Button>
        </Card>

        <Thread patient={p} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <GoalsForm patientId={id} />
        <VitalsTargetsForm patientId={id} />
      </div>
      <PatientWorries patientId={id} />
      <div className="grid lg:grid-cols-2 gap-4">
        <MealScoreCard patientId={id} />
        <MealGoalForm patientId={id} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <ProtocolProgress patientId={id} pro />
        <PainCard patientId={id} refreshKey={actesKey} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <ActesCard patientId={id} refreshKey={actesKey} />
        <Card title="Rendez-vous" testid="appointments-card">
          {s.rendez_vous.length === 0 && <div className="text-[12px] text-[#0A1426]/60">Aucun rendez-vous.</div>}
          {s.rendez_vous.slice(0, 6).map((a) => (
            <div key={a.id} data-testid={`appt-${a.id}`} className="flex items-center justify-between gap-2 py-1.5 border-b border-[#F5F3EE] last:border-0 text-[13px] text-[#0A1426]">
              <span>{new Date(a.datetime).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })} · {a.type === "teleconsult" ? "Téléconsultation (ancien)" : "Présentiel"} · <b>{a.statut}</b></span>
            </div>
          ))}
          <div className="mt-2 text-[11px] text-[#0A1426]/60">Les séances vidéo passent désormais par le module Télésoin (contrôles de conformité, TMK).</div>
        </Card>
      </div>
      <MealHistoryChart patientId={id} />
      <MealPhotosCard patientId={id} />

      {prog && <ProgrammeBuilder patientId={id} onClose={() => setProg(false)} onDone={load} />}
      {tele && <TelesoinPanel patientId={id} onClose={() => setTele(false)} onChanged={teleChanged} />}

      <Card title="Chronologie du suivi pluridisciplinaire" testid="timeline">
        <ol className="relative border-l-2 border-[#E4E0D5] ml-2 flex flex-col gap-3">
          {s.chronologie.map((e, i) => (
            <li key={i} className="pl-4">
              <span className={`absolute -left-[7px] w-3 h-3 rounded-full ${e.type === "bilan" ? "bg-[#D4AF5A]" : e.type === "rdv" ? "bg-[#3a8acb]" : e.type === "note" ? "bg-[#0A1426]" : "bg-[#2BA3A0]"}`} />
              <div className="text-[11px] text-[#0A1426]/60">{new Date(e.date).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })} · {e.type}{e.auteur ? ` · ${e.auteur}` : ""}</div>
              <div className="text-[13px] text-[#0A1426]">{e.texte}</div>
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}
